import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Trash2, Edit2, X, Check, Eye, Clock, Tag, Search, AlignLeft, AlignCenter, AlignRight, Maximize2, Type, Move } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';

Quill.register('modules/imageResize', ImageResize);

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  published: 'bg-green-100 text-green-800',
  draft: 'bg-yellow-100 text-yellow-800',
};

/* ── Image Context Menu (Word-like right-click menu) ── */
function ImageContextMenu({ x, y, imgEl, onClose, onLayoutChange }) {
  const [tab, setTab] = useState('layout');
  const [widthVal, setWidthVal] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    if (imgEl) setWidthVal(imgEl.style.width ? parseInt(imgEl.style.width) : imgEl.offsetWidth);
  }, [imgEl]);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [onClose]);

  if (!imgEl) return null;

  const currentLayout = (() => {
    const s = imgEl.style;
    if (s.float === 'left') return 'float-left';
    if (s.float === 'right') return 'float-right';
    if (s.display === 'block' && s.marginLeft === 'auto' && s.marginRight === 'auto' && s.width === '100%') return 'full-width';
    if (s.display === 'block' && s.marginLeft === 'auto' && s.marginRight === 'auto') return 'center';
    return 'inline';
  })();

  const layouts = [
    { id: 'inline', label: 'Inline', desc: 'In line with text', icon: <Type size={16} /> },
    { id: 'float-left', label: 'Float Left', desc: 'Text wraps right', icon: <AlignLeft size={16} /> },
    { id: 'center', label: 'Center', desc: 'Centered block', icon: <AlignCenter size={16} /> },
    { id: 'float-right', label: 'Float Right', desc: 'Text wraps left', icon: <AlignRight size={16} /> },
    { id: 'full-width', label: 'Full Width', desc: '100% container width', icon: <Maximize2 size={16} /> },
  ];

  const applyLayout = (layoutId) => {
    // Reset all layout styles
    imgEl.style.float = '';
    imgEl.style.display = '';
    imgEl.style.marginLeft = '';
    imgEl.style.marginRight = '';
    imgEl.style.clear = '';

    switch (layoutId) {
      case 'float-left':
        imgEl.style.float = 'left';
        imgEl.style.marginRight = '16px';
        imgEl.style.marginBottom = '8px';
        break;
      case 'float-right':
        imgEl.style.float = 'right';
        imgEl.style.marginLeft = '16px';
        imgEl.style.marginBottom = '8px';
        break;
      case 'center':
        imgEl.style.display = 'block';
        imgEl.style.marginLeft = 'auto';
        imgEl.style.marginRight = 'auto';
        break;
      case 'full-width':
        imgEl.style.display = 'block';
        imgEl.style.marginLeft = 'auto';
        imgEl.style.marginRight = 'auto';
        imgEl.style.width = '100%';
        break;
      default: // inline
        break;
    }
    if (onLayoutChange) onLayoutChange();
  };

  const applyResize = () => {
    const w = parseInt(widthVal);
    if (w && w > 20) {
      imgEl.style.width = w + 'px';
      imgEl.style.height = 'auto';
      if (onLayoutChange) onLayoutChange();
    }
  };

  const deleteImage = () => {
    imgEl.remove();
    if (onLayoutChange) onLayoutChange();
    onClose();
  };

  // Position clamping
  const menuW = 320, menuH = 360;
  const clampedX = Math.min(x, window.innerWidth - menuW - 16);
  const clampedY = Math.min(y, window.innerHeight - menuH - 16);

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden text-gray-900"
      style={{ left: clampedX, top: clampedY, width: menuW }}
      data-testid="image-context-menu"
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {[
          { id: 'layout', label: 'Position' },
          { id: 'size', label: 'Size' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === t.id ? 'text-gray-900 border-b-2 border-orange-500 bg-white' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Layout Tab */}
      {tab === 'layout' && (
        <div className="p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-3">Picture Position</p>
          <div className="grid grid-cols-5 gap-1.5 mb-4">
            {layouts.map(l => (
              <button
                key={l.id}
                onClick={() => applyLayout(l.id)}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all text-center ${
                  currentLayout === l.id
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700'
                }`}
                title={l.desc}
                data-testid={`layout-${l.id}`}
              >
                {l.icon}
                <span className="text-[9px] font-bold leading-tight">{l.label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400">{layouts.find(l => l.id === currentLayout)?.desc}</p>
          {/* Delete */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <button
              onClick={deleteImage}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              data-testid="delete-image-ctx"
            >
              <Trash2 size={13} /> Remove Picture
            </button>
          </div>
        </div>
      )}

      {/* Size Tab */}
      {tab === 'size' && (
        <div className="p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-3">Resize Picture</p>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1">
              <label className="text-[10px] text-gray-400 uppercase font-bold">Width (px)</label>
              <input
                type="number"
                value={widthVal}
                onChange={(e) => setWidthVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyResize()}
                min={20}
                className="w-full mt-1 px-2.5 py-1.5 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="resize-width-input"
              />
            </div>
            <div className="flex-1 pt-4">
              <p className="text-xs text-gray-400">Height adjusts automatically to keep aspect ratio</p>
            </div>
          </div>
          <button
            onClick={applyResize}
            className="w-full px-3 py-2 text-xs font-bold uppercase tracking-wider bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            data-testid="apply-resize-btn"
          >
            Apply Size
          </button>
          {/* Quick size presets */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Quick Presets</p>
            <div className="flex gap-1.5">
              {[200, 400, 600, '100%'].map(preset => (
                <button
                  key={preset}
                  onClick={() => {
                    if (preset === '100%') {
                      imgEl.style.width = '100%';
                    } else {
                      imgEl.style.width = preset + 'px';
                    }
                    imgEl.style.height = 'auto';
                    setWidthVal(preset === '100%' ? imgEl.offsetWidth : preset);
                    if (onLayoutChange) onLayoutChange();
                  }}
                  className="flex-1 px-2 py-1.5 text-[10px] font-bold border border-gray-200 rounded hover:border-gray-400 transition-colors"
                  data-testid={`preset-${preset}`}
                >
                  {preset === '100%' ? 'Full' : preset + 'px'}
                </button>
              ))}
            </div>
          </div>
          {/* Delete */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={deleteImage}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <Trash2 size={13} /> Remove Picture
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ArticlesManager({ token }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', body: '', featured_image: '',
    author: 'Total Composites', categories: '', tags: '', status: 'draft'
  });

  const authToken = token || localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const quillRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState({ show: false, x: 0, y: 0, imgEl: null });

  // Right-click handler for images in editor
  useEffect(() => {
    if (!showEditor) { setCtxMenu({ show: false, x: 0, y: 0, imgEl: null }); return; }
    const handler = (e) => {
      const img = e.target.closest('.ql-editor img');
      if (img) {
        e.preventDefault();
        setCtxMenu({ show: true, x: e.clientX, y: e.clientY, imgEl: img });
      }
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [showEditor]);

  // Image upload handler for Quill
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        alert('Image must be under 10MB');
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/api/portal/admin/articles/upload-image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        });
        const data = await res.json();
        if (data.url) {
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', `${API}${data.url}`);
            quill.setSelection(range.index + 1);
          }
        }
      } catch (err) {
        console.error('Image upload failed:', err);
        alert('Failed to upload image. Please try again.');
      }
      setUploading(false);
    };
  }, [authToken]);

  // Featured image upload
  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be under 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/portal/admin/articles/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        setForm(f => ({ ...f, featured_image: `${API}${data.url}` }));
      }
    } catch (err) {
      console.error('Featured image upload failed:', err);
    }
    setUploading(false);
  };

  // Quill modules
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'],
    },
    clipboard: { matchVisual: false },
  }), [imageHandler]);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'blockquote', 'code-block', 'link', 'image',
    'width', 'height', 'style',
  ];

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/admin/articles`, { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } });
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const resetForm = () => {
    setForm({ title: '', slug: '', excerpt: '', body: '', featured_image: '', author: 'Total Composites', categories: '', tags: '', status: 'draft' });
    setEditingArticle(null);
    setShowEditor(false);
  };

  const handleEdit = (article) => {
    setForm({
      title: article.title || '',
      slug: article.slug || '',
      excerpt: article.excerpt || '',
      body: article.body || '',
      featured_image: article.featured_image || '',
      author: article.author || 'Total Composites',
      categories: (article.categories || []).join(', '),
      tags: (article.tags || []).join(', '),
      status: article.status || 'draft',
    });
    setEditingArticle(article);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Title is required');
    // Read the latest body HTML directly from the Quill editor DOM to capture layout/resize changes
    let bodyHtml = form.body;
    const editor = quillRef.current?.getEditor?.();
    if (editor) bodyHtml = editor.root.innerHTML;
    const payload = {
      ...form,
      body: bodyHtml,
      categories: form.categories.split(',').map(s => s.trim()).filter(Boolean),
      tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editingArticle) {
        await fetch(`${API}/api/portal/admin/articles/${editingArticle.id}`, {
          method: 'PUT', headers, body: JSON.stringify(payload)
        });
      } else {
        await fetch(`${API}/api/portal/admin/articles`, {
          method: 'POST', headers, body: JSON.stringify(payload)
        });
      }
      resetForm();
      fetchArticles();
    } catch (err) {
      console.error('Failed to save article:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    try {
      await fetch(`${API}/api/portal/admin/articles/${id}`, { method: 'DELETE', headers });
      fetchArticles();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleToggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    try {
      await fetch(`${API}/api/portal/admin/articles/${article.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus })
      });
      fetchArticles();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const filteredArticles = articles.filter(a =>
    a.title?.toLowerCase().includes(search.toLowerCase()) ||
    a.categories?.some(c => c.toLowerCase().includes(search.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="p-4 sm:p-8 text-gray-900" data-testid="articles-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900">Articles</h2>
          <p className="text-sm text-gray-500 mt-1">
            {articles.length} article{articles.length !== 1 ? 's' : ''} &middot; {articles.filter(a => a.status === 'published').length} published
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowEditor(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white text-xs font-bold uppercase tracking-wider rounded hover:bg-gray-800 transition-colors"
          data-testid="new-article-btn"
        >
          <Plus size={14} /> New Article
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search articles..."
          className="w-full pl-9 pr-4 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          data-testid="articles-search"
        />
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 px-4 overflow-y-auto" data-testid="article-editor-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mb-10 text-gray-900">
            {/* Editor Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">
                {editingArticle ? 'Edit Article' : 'New Article'}
              </h3>
              <div className="flex items-center gap-3">
                {uploading && (
                  <span className="text-xs text-orange-600 font-medium animate-pulse">Uploading image...</span>
                )}
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Editor Body */}
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Article title"
                  className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="article-title-input"
                />
              </div>

              {/* Slug + Author */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Slug (auto-generated if empty)</label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                    placeholder="article-url-slug"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="article-slug-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Author</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm(f => ({ ...f, author: e.target.value }))}
                    placeholder="Author name"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="article-author-input"
                  />
                </div>
              </div>

              {/* Featured Image — Upload + URL */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Featured Image</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" data-testid="featured-image-upload">
                    <Plus size={12} /> Upload Image
                    <input type="file" accept="image/*" onChange={handleFeaturedImageUpload} className="hidden" />
                  </label>
                  <span className="text-xs text-gray-400">or</span>
                  <input
                    type="text"
                    value={form.featured_image}
                    onChange={(e) => setForm(f => ({ ...f, featured_image: e.target.value }))}
                    placeholder="Paste image URL"
                    className="flex-1 px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="article-image-input"
                  />
                </div>
                {form.featured_image && (
                  <div className="mt-2 relative inline-block">
                    <img src={form.featured_image} alt="Featured" className="h-24 rounded-lg object-cover border border-gray-200" />
                    <button
                      onClick={() => setForm(f => ({ ...f, featured_image: '' }))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Excerpt (short preview)</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))}
                  placeholder="Brief summary shown on the blog page..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
                  data-testid="article-excerpt-input"
                />
              </div>

              {/* Body — WYSIWYG Editor */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Article Content
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="article-body-editor">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.body}
                    onChange={(val) => setForm(f => ({ ...f, body: val }))}
                    modules={modules}
                    formats={formats}
                    placeholder="Start writing your article..."
                    style={{ minHeight: 320 }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Use the toolbar to format text, add headings, lists, links, and images. <strong>Right-click</strong> any image to position, resize, or remove it.
                </p>
              </div>

              {/* Image Context Menu */}
              {ctxMenu.show && (
                <ImageContextMenu
                  x={ctxMenu.x}
                  y={ctxMenu.y}
                  imgEl={ctxMenu.imgEl}
                  onClose={() => setCtxMenu({ show: false, x: 0, y: 0, imgEl: null })}
                  onLayoutChange={() => {}}
                />
              )}

              {/* Categories + Tags */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Categories (comma-separated)</label>
                  <input
                    type="text"
                    value={form.categories}
                    onChange={(e) => setForm(f => ({ ...f, categories: e.target.value }))}
                    placeholder="Technical, News, Updates"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="article-categories-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                    placeholder="truck camper, composite, overlanding"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="article-tags-input"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                  className="px-3 py-2 text-sm text-gray-900 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="article-status-select"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {/* Editor Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={resetForm} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-gray-900">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={uploading}
                className="flex items-center gap-2 px-5 py-2 bg-black text-white text-xs font-bold uppercase tracking-wider rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
                data-testid="article-save-btn"
              >
                <Check size={14} />
                {editingArticle ? 'Update' : 'Create'} Article
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Articles Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-50 rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <div className="text-4xl mb-3 opacity-30">&#9998;</div>
          <h3 className="text-base font-bold text-gray-500 mb-1">
            {search ? 'No matching articles' : 'No articles yet'}
          </h3>
          <p className="text-sm text-gray-400">
            {search ? 'Try a different search term.' : 'Click "New Article" to create your first post.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="articles-list">
          {filteredArticles.map(article => (
            <div
              key={article.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid={`admin-article-${article.id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {article.featured_image && (
                  <img src={article.featured_image} alt="" className="w-12 h-12 rounded object-cover shrink-0 border border-gray-200" />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${STATUS_STYLES[article.status] || STATUS_STYLES.draft}`}>
                      {article.status}
                    </span>
                    {article.categories?.slice(0, 2).map(c => (
                      <span key={c} className="px-1.5 py-0.5 text-xs text-gray-500 bg-gray-200 rounded">{c}</span>
                    ))}
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 truncate">{article.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {article.author} &middot; {formatDate(article.publish_date)} &middot; {article.read_time} min read
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => handleToggleStatus(article)}
                  className={`p-1.5 rounded text-xs font-bold ${article.status === 'published' ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                  title={article.status === 'published' ? 'Unpublish' : 'Publish'}
                  data-testid={`toggle-status-${article.id}`}
                >
                  {article.status === 'published' ? <Eye size={14} /> : <Check size={14} />}
                </button>
                <button
                  onClick={() => handleEdit(article)}
                  className="p-1.5 rounded text-blue-600 hover:bg-blue-50"
                  title="Edit"
                  data-testid={`edit-article-${article.id}`}
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(article.id)}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50"
                  title="Delete"
                  data-testid={`delete-article-${article.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quill editor styles override */}
      <style>{`
        .ql-toolbar.ql-snow { border-color: #e5e7eb !important; border-bottom: 1px solid #e5e7eb !important; background: #f9fafb; }
        .ql-container.ql-snow { border-color: #e5e7eb !important; font-family: inherit; font-size: 14px; min-height: 300px; }
        .ql-editor { min-height: 300px; line-height: 1.7; color: #111827 !important; }
        .ql-editor p { margin-bottom: 0.75em; }
        .ql-editor h1 { font-size: 1.75em; font-weight: 900; margin: 1em 0 0.5em; }
        .ql-editor h2 { font-size: 1.35em; font-weight: 800; margin: 1em 0 0.4em; }
        .ql-editor h3 { font-size: 1.1em; font-weight: 700; margin: 0.8em 0 0.3em; }
        .ql-editor img { max-width: 100%; border-radius: 8px; margin: 1em 0; cursor: pointer; transition: outline 0.15s; }
        .ql-editor img:hover { outline: 2px solid #d1d5db; outline-offset: 2px; }
        .ql-editor img.ql-resize-selected { outline: 2px solid #E8520A; outline-offset: 2px; }
        .ql-editor img[style*="float: left"] { margin-right: 16px !important; margin-bottom: 8px !important; }
        .ql-editor img[style*="float: right"] { margin-left: 16px !important; margin-bottom: 8px !important; }
        .ql-editor::after { content: ''; display: table; clear: both; }
        .ql-editor blockquote { border-left: 3px solid #E8520A; padding-left: 1em; color: #6b7280; font-style: italic; }
        .ql-snow .ql-tooltip { z-index: 9999; }
        .ql-snow .ql-picker { font-size: 13px; }
      `}</style>
    </div>
  );
}
